import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { timeAgo } from "@/lib/format";
import { ChatCircle, ChatText, ClipboardText, CaretDown } from "@phosphor-icons/react";

const STATUSES = [
  { value: "new", label: "New", tone: "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300" },
  { value: "in_progress", label: "In Progress", tone: "bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300" },
  { value: "awaiting_response", label: "Awaiting Response", tone: "bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300" },
  { value: "resolved", label: "Resolved", tone: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300" },
  { value: "closed", label: "Closed", tone: "bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300" },
];
const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("new");
  const [busy, setBusy] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const load = () => api.get("/enquiries").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openDetail = (e) => {
    setSelected(e);
    setNotes(e.admin_notes || "");
    setStatus(e.status || "new");
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/enquiries/${id}`, { status: newStatus });
      toast.success(`Status: ${statusMeta(newStatus).label}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const saveDetail = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.patch(`/enquiries/${selected.id}`, { status, admin_notes: notes });
      toast.success("Enquiry updated");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const counts = STATUSES.map((s) => ({ ...s, count: items.filter((i) => (i.status || "new") === s.value).length }));
  const activeItems = items.filter((i) => (i.status || "new") !== "closed");
  const closedItems = items.filter((i) => (i.status || "new") === "closed");

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Admin · Investor Enquiries</div>
          <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
            <ChatCircle size={28} weight="fill" className="text-emerald-600" />
            Enquiries <span className="text-muted-foreground font-normal">({items.length})</span>
          </h1>
        </div>
      </div>

      {/* Status pill overview */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="enquiry-status-summary">
        {counts.map((s) => (
          <div key={s.value} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${s.tone}`}>
            {s.label} · {s.count}
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/50 hover:bg-secondary/50">
              <TableHead>Investor</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Received</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeItems.map((e) => {
              const meta = statusMeta(e.status || "new");
              return (
                <TableRow key={e.id} data-testid={`enquiry-row-${e.id}`}>
                  <TableCell>
                    <div className="text-sm font-medium">{e.user?.name}</div>
                    <div className="text-xs text-muted-foreground">{e.user?.email}</div>
                  </TableCell>
                  <TableCell>
                    <Link to={`/companies/${e.company_id}`} className="hover:underline text-sm">{e.company?.name}</Link>
                  </TableCell>
                  <TableCell className="text-sm font-medium max-w-[240px] truncate">{e.subject}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{timeAgo(e.created_at)}</TableCell>
                  <TableCell>
                    <Select value={e.status || "new"} onValueChange={(v) => updateStatus(e.id, v)}>
                      <SelectTrigger className={`h-8 w-[170px] text-xs font-semibold ${meta.tone} border-transparent`} data-testid={`enquiry-status-${e.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => openDetail(e)} data-testid={`enquiry-open-${e.id}`}>
                      <ChatText size={13} className="mr-1" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {activeItems.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                {items.length === 0 ? "No enquiries yet." : "No open enquiries — all requests closed."}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {closedItems.length > 0 && (
        <div className="mt-6 rounded-lg border border-border bg-card overflow-hidden" data-testid="closed-enquiries-section">
          <button
            onClick={() => setShowClosed((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-secondary/40 hover:bg-secondary/60 transition-colors text-left"
            data-testid="toggle-closed-enquiries"
          >
            <div className="flex items-center gap-2">
              <CaretDown size={14} className={`transition-transform ${showClosed ? "rotate-0" : "-rotate-90"}`} />
              <span className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Closed enquiries</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-semibold">
                {closedItems.length}
              </span>
            </div>
            <span className="text-[11px] text-muted-foreground">{showClosed ? "Hide" : "Show"}</span>
          </button>
          {showClosed && (
            <Table>
              <TableBody>
                {closedItems.map((e) => (
                  <TableRow key={e.id} data-testid={`closed-enquiry-row-${e.id}`} className="opacity-80">
                    <TableCell>
                      <div className="text-sm font-medium">{e.user?.name}</div>
                      <div className="text-xs text-muted-foreground">{e.user?.email}</div>
                    </TableCell>
                    <TableCell>
                      <Link to={`/companies/${e.company_id}`} className="hover:underline text-sm">{e.company?.name}</Link>
                    </TableCell>
                    <TableCell className="text-sm font-medium max-w-[240px] truncate">{e.subject}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{timeAgo(e.updated_at || e.created_at)}</TableCell>
                    <TableCell>
                      <Select value="closed" onValueChange={(v) => updateStatus(e.id, v)}>
                        <SelectTrigger className={`h-8 w-[170px] text-xs font-semibold ${statusMeta("closed").tone} border-transparent`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openDetail(e)}>
                        <ChatText size={13} className="mr-1" /> View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="enquiry-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2">
              <ChatCircle size={20} weight="fill" className="text-emerald-600" /> Enquiry
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Investor</div>
                  <div className="font-medium">{selected.user?.name}</div>
                  <div className="text-xs text-muted-foreground">{selected.user?.email}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Company</div>
                  <Link to={`/companies/${selected.company_id}`} className="font-medium hover:underline">{selected.company?.name}</Link>
                  <div className="text-xs text-muted-foreground">Received {timeAgo(selected.created_at)}</div>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Subject</div>
                <div className="text-sm font-medium">{selected.subject}</div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Message</div>
                <div className="text-sm text-foreground/90 whitespace-pre-wrap rounded-lg border border-border bg-secondary/30 p-3 max-h-48 overflow-y-auto">{selected.message}</div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Status</div>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="w-[240px]" data-testid="enquiry-detail-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
                  <ClipboardText size={12} /> Internal notes <span className="normal-case text-muted-foreground/70">(only visible to admin/team)</span>
                </div>
                <Textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add follow-up notes, scheduled call time, etc."
                  data-testid="enquiry-detail-notes"
                />
              </div>

              {selected.updated_at && (
                <div className="text-[11px] text-muted-foreground">
                  Last updated {timeAgo(selected.updated_at)} by {selected.updated_by_name || "team"}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
            <Button onClick={saveDetail} disabled={busy} data-testid="enquiry-save-btn" className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
